import React from 'react';
import { motion } from 'framer-motion';
import { WifiOff, RefreshCw } from 'lucide-react';
import Logo from './Logo';

/**
 * ServiceUnavailable Component
 * 
 * Wyświetlany gdy frontend nie może połączyć się z backendem.
 * Tłumaczenia są statyczne (nie z bazy) ponieważ backend jest niedostępny.
 * 
 * Języki wykrywane są na podstawie przeglądarki użytkownika.
 */

// Statyczne tłumaczenia - nie ciągniemy z backendu bo jest niedostępny
const translations = {
  pl: {
    title: 'Przerwa techniczna',
    subtitle: 'Przepraszamy za utrudnienia',
    message: 'Nasza strona jest chwilowo niedostępna. Pracujemy nad tym, aby jak najszybciej przywrócić pełną funkcjonalność.',
    backSoon: 'Wracamy już za chwilę!',
    refresh: 'Odśwież stronę',
    contact: 'W razie pytań skontaktuj się z nami:',
    contact2: 'kontakt@avepark.pl lub +48 797 254 850'
  },
  en: {
    title: 'Service Temporarily Unavailable',
    subtitle: 'We apologize for the inconvenience',
    message: 'Our website is currently undergoing maintenance. We are working hard to restore full functionality as soon as possible.',
    backSoon: "We'll be back shortly!",
    refresh: 'Refresh page',
    contact: 'If you have any questions, please contact us:',
    contact2: 'kontakt@avepark.pl or +48 797 254 850'
  }
};

// Wykryj język przeglądarki
const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Jeśli polski lub podobny, użyj polskiego
  if (langCode === 'pl') return 'pl';
  
  // Dla innych języków użyj angielskiego jako fallback
  return 'en';
};

const ServiceUnavailable = ({ onRetry }) => {
  const lang = getBrowserLanguage();
  const t = translations[lang];
  
  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-6">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-lg w-full"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="inline-block"
          >
            {/* AvePark Logo - używa oficjalnego komponentu Logo */}
            <Logo className="h-16 w-auto text-white" variant="light" />
          </motion.div>
        </div>

        {/* Main card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-10 text-center"
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, delay: 0.4 }}
            className="w-20 h-20 mx-auto mb-6 bg-primary/20 rounded-full flex items-center justify-center"
          >
            <WifiOff className="w-10 h-10 text-primary" />
          </motion.div>

          {/* Title */}
          <h1 className="text-2xl md:text-3xl font-['Syne'] font-bold text-white mb-2">
            {t.title}
          </h1>
          
          <p className="text-primary font-medium mb-6">
            {t.subtitle}
          </p>

          {/* Message */}
          <p className="text-gray-400 mb-6 leading-relaxed">
            {t.message}
          </p>

          {/*

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary font-medium mb-8"
          >
            <span className="bg-primary rounded-full animate-pulse" />
            {t.backSoon}
          </motion.div>
          */}

          {/* Refresh button */}
          <motion.button
            whileHover={{ scale: 1.00 }}
            whileTap={{ scale: 1.01 }}
            onClick={handleRefresh}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary font-medium"
          >
            <RefreshCw className="w-5 h-5" />
            {t.refresh}
          </motion.button>

          {/* Contact info */}
          <p className="text-gray-500 text-sm mt-6">
            {t.contact}
          </p>
          <p className="text-white font-semibold text-sm mt-1">
            {t.contact2}
          </p>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center text-gray-600 text-sm mt-8"
        >
          © {new Date().getFullYear()} AvePark. All rights reserved.
        </motion.p>
      </motion.div>
    </div>
  );
};

export default ServiceUnavailable;
